import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAdminOverview, type AdminOverview } from "@/api/reviewItems";
import { IntentLauncher } from "./components/intents/IntentLauncher";
import { itemTypeLabel, statusLabel } from "./labels";

export default function AdminOverviewPage() {
  const q = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => {
      const r = await getAdminOverview();
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    refetchInterval: 60_000,
  });

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1100 }}>
      <header style={{ marginBottom: "var(--sp-3)" }}>
        <h1>Обзор</h1>
        <p className="mt-text-tight mt-muted">
          Две главные метрики: «Очередь ревью» (что я ещё не одобрил) и
          «Активный словарь» (что resolver реально использует при переводе).
          См. <code>docs/review-ui-redesign.md § 10.4</code> — почему они
          разные.
        </p>
      </header>

      <IntentLauncher />

      {q.isLoading ? (
        <div className="mt-empty">Загружаем…</div>
      ) : q.isError || !q.data ? (
        <div className="mt-empty mt-empty--error">
          {(q.error as Error | undefined)?.message ?? "Ошибка"}
        </div>
      ) : (
        <>
          <ReviewQueueSection data={q.data} />
          <ActiveDictionarySection data={q.data} />
          <FeedbackSection data={q.data} />
          <LlmSuggestionsSection data={q.data} />
        </>
      )}
    </div>
  );
}

function ReviewQueueSection({ data }: { data: AdminOverview }) {
  const byStatus = new Map(
    data.review_items.by_status.map((b) => [b.status, b.count]),
  );
  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Очередь ревью</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-text-tight mt-muted">review_items</span>
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Stat
            label="Открыто"
            value={byStatus.get("open") ?? 0}
            to="/admin/review?status=open"
          />
          <Stat
            label="Принято мной"
            value={byStatus.get("approved") ?? 0}
            help="это решения reviewer'а — не путать с активным словарём (~20k)"
            to="/admin/review?status=approved"
          />
          <Stat
            label="На втором ревью"
            value={byStatus.get("needs_second_review") ?? 0}
            to="/admin/review?status=needs_second_review"
          />
          <Stat
            label="Отклонено"
            value={byStatus.get("rejected") ?? 0}
            to="/admin/review?status=rejected"
          />
        </div>
        <div>
          <div className="mt-text-tight mt-muted" style={{ marginBottom: 6 }}>
            Открытые задачи по типам:
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {data.review_items.open_by_type.map((b) => (
              <Link
                key={b.item_type}
                to={`/admin/review?status=open&item_type=${encodeURIComponent(b.item_type)}`}
                className="mt-badge mt-badge--info mt-badge--no-dot"
                style={{ textDecoration: "none" }}
              >
                {itemTypeLabel(b.item_type)} · {b.count}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ActiveDictionarySection({ data }: { data: AdminOverview }) {
  const ab = data.abbreviations.by_status.reduce(
    (sum, b) => (b.status === "approved" ? sum + b.count : sum),
    0,
  );
  const tal = data.term_aliases.by_status.reduce(
    (sum, b) => (b.status === "approved" ? sum + b.count : sum),
    0,
  );
  const talForbidden = data.term_aliases.by_status.reduce(
    (sum, b) => (b.status === "forbidden" ? sum + b.count : sum),
    0,
  );
  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Активный словарь</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-text-tight mt-muted">
          glossary_terms / abbreviations / term_aliases
        </span>
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Stat
            label="Glossary terms approved"
            value={data.glossary_terms.total_active}
            help="всего одобренных пар EN↔RU в активном словаре (часть пришла через ревью, часть из seed-ETL)"
            to="/admin/terminology?item_type=glossary_term&status=approved"
          />
          <Stat
            label="↳ hard enforcement"
            value={data.glossary_terms.hard_active}
            help="constraint_eligible=true + trusted ru_term_source + morph_safe — будут заменены автоматически"
            to="/admin/terminology?item_type=glossary_term&status=approved&mode=hard"
          />
          <Stat
            label="↳ hint only"
            value={data.glossary_terms.hint_active}
            help="одобрено, но без автозамены — только подсказка LLM-переводчику"
            to="/admin/terminology?item_type=glossary_term&status=approved&mode=hint"
          />
          <Stat
            label="Abbreviations active"
            value={ab}
            help="одобренные сокращения с RU-расшифровкой"
            to="/admin/terminology?item_type=abbreviation&status=approved"
          />
          <Stat
            label="Term aliases approved"
            value={tal}
            help="вариации/синонимы одобренных терминов (помогают matcher'у)"
            to="/admin/terminology?item_type=term_alias&status=approved"
          />
          {talForbidden > 0 && (
            <Stat
              label="Aliases forbidden"
              value={talForbidden}
              help="запрещённые формы — переводчик получит warning"
              to="/admin/terminology?item_type=term_alias&status=forbidden"
            />
          )}
        </div>
        <div className="mt-text-tight mt-muted">
          {data.glossary_terms.total_active.toLocaleString("ru-RU")} активных
          ≠{" "}
          {data.review_items.by_status.find((b) => b.status === "approved")
            ?.count ?? 0}{" "}
          принятых через ревью. Большая часть приходит из seed-pipeline'ов
          напрямую как approved.
        </div>
      </div>
    </section>
  );
}

function FeedbackSection({ data }: { data: AdminOverview }) {
  const breakdown = data.translation_feedback?.by_status ?? [];
  const byStatus = new Map(breakdown.map((b) => [b.status, b.count]));
  const total = breakdown.reduce((s, b) => s + b.count, 0);
  if (total === 0) return null;
  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Замечания к переводам</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-text-tight mt-muted">translation_feedback</span>
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          <Stat
            label="Открыто"
            value={byStatus.get("open") ?? 0}
            to="/admin/feedback?status=open"
          />
          <Stat
            label="В работе"
            value={byStatus.get("in_progress") ?? 0}
            to="/admin/feedback?status=in_progress"
          />
          <Stat
            label="Решено"
            value={byStatus.get("resolved") ?? 0}
            to="/admin/feedback?status=resolved"
          />
          <Stat
            label="Won't fix"
            value={byStatus.get("wontfix") ?? 0}
            to="/admin/feedback?status=wontfix"
          />
        </div>
      </div>
    </section>
  );
}

function LlmSuggestionsSection({ data }: { data: AdminOverview }) {
  if (!data.llm_suggestions.by_status.length) return null;
  const total = data.llm_suggestions.by_status.reduce(
    (s, b) => s + b.count,
    0,
  );
  if (total === 0) return null;
  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>LLM Suggestions</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-text-tight mt-muted">всего: {total}</span>
      </div>
      <div className="rf-section__body">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {data.llm_suggestions.by_status.map((b, i) => (
            <span
              key={i}
              className={
                "mt-badge mt-badge--no-dot " +
                (b.status === "completed"
                  ? "mt-badge--success"
                  : b.status === "failed"
                    ? "mt-badge--error"
                    : "mt-badge--neutral")
              }
            >
              {b.status} ({b.mode}): {b.count}
            </span>
          ))}
        </div>
        <div className="mt-text-tight mt-muted" style={{ marginTop: 8 }}>
          Подробности — <Link to="/admin/llm-jobs">/admin/llm-jobs</Link>.
        </div>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  help,
  to,
}: {
  label: string;
  value: number;
  help?: string;
  to?: string;
}) {
  const cardStyle = {
    padding: "12px 14px",
    background: "var(--panel)",
    borderRadius: 8,
    border: "1px solid var(--border-default, #d0d5dd)",
    display: "block",
    color: "inherit",
    textDecoration: "none",
    transition: "border-color 120ms, transform 120ms",
  } as const;
  const labelEl = (
    <div
      className="mt-text-tight mt-muted"
      style={{ marginBottom: 4, textTransform: "none", letterSpacing: 0 }}
    >
      {label}
    </div>
  );
  const valueEl = (
    <div style={{ fontSize: "var(--fs-xl, 24px)", fontWeight: 600 }}>
      {value.toLocaleString("ru-RU")}
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="mt-card mt-card--clickable" title={help} style={cardStyle}>
        {labelEl}
        {valueEl}
      </Link>
    );
  }
  return (
    <div className="mt-card" title={help} style={cardStyle}>
      {labelEl}
      {valueEl}
    </div>
  );
}

// statusLabel переиспользуется уже импортированным.
void statusLabel;
